import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useSpring } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowUpRight, Link2, Check } from "lucide-react";
import { FaLinkedinIn, FaXTwitter } from "react-icons/fa6";
import { WRITING, PROFILE } from "@/data/content";
import { BackToTop } from "@/components/portfolio/BackToTop";

const setMeta = (property, content, attr = "property") => {
  let tag = document.head.querySelector(`meta[${attr}="${property}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, property);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
};

const SITE = "https://www.cathemagawa.com";

const setJsonLd = (name, data) => {
  let tag = document.head.querySelector(`script[data-jsonld="${name}"]`);
  if (!tag) {
    tag = document.createElement("script");
    tag.type = "application/ld+json";
    tag.setAttribute("data-jsonld", name);
    document.head.appendChild(tag);
  }
  tag.textContent = JSON.stringify(data);
};

const removeJsonLd = (name) => {
  const tag = document.head.querySelector(`script[data-jsonld="${name}"]`);
  if (tag) tag.remove();
};

export default function Article() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const article = WRITING.find((w) => w.slug === slug);
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.3 });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (!article) return;
    const prevTitle = document.title;
    const url = window.location.href;
    document.title = `${article.title} — Catherine Magawa`;
    setMeta("og:title", `${article.title} — Catherine Magawa`);
    setMeta("og:description", article.excerpt);
    setMeta("og:type", "article");
    setMeta("og:url", url);
    setMeta("twitter:card", "summary_large_image", "name");
    setMeta("twitter:title", `${article.title} — Catherine Magawa`, "name");
    setMeta("twitter:description", article.excerpt, "name");
    setMeta("description", article.excerpt, "name");

    const canonical = `${SITE}/writing/${article.slug}`;
    const ogImage = `${process.env.REACT_APP_BACKEND_URL}/api/og-image/${article.slug}.png`;
    const parsed = new Date(article.date);
    const published = isNaN(parsed) ? undefined : parsed.toISOString();
    setJsonLd("article", {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: article.title,
      description: article.excerpt,
      image: ogImage,
      ...(published ? { datePublished: published, dateModified: published } : {}),
      articleSection: article.tag,
      mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
      url: canonical,
      author: {
        "@type": "Person",
        name: PROFILE.name,
        jobTitle: PROFILE.role,
        url: SITE,
        image: PROFILE.portrait,
      },
      publisher: {
        "@type": "Person",
        name: PROFILE.name,
        url: SITE,
      },
    });

    setJsonLd("breadcrumb", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "Writing", item: `${SITE}/#writing` },
        { "@type": "ListItem", position: 3, name: article.title, item: canonical },
      ],
    });

    return () => {
      document.title = prevTitle;
      removeJsonLd("article");
      removeJsonLd("breadcrumb");
    };
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-[#eef0e9] flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="font-serif-display text-4xl text-[#16324f]">Article not found</h1>
          <Link to="/" className="inline-flex items-center gap-2 mt-6 font-mono-plex text-[12px] uppercase tracking-[0.14em] text-[#b5423a]">
            <ArrowLeft size={14} /> Back to writing
          </Link>
        </div>
      </div>
    );
  }

  const idx = WRITING.findIndex((w) => w.slug === slug);
  const next = WRITING[(idx + 1) % WRITING.length];

  const shareUrl = `${process.env.REACT_APP_BACKEND_URL}/api/share/${slug}`;
  const shareText = `${article.title} — Catherine Magawa`;
  const linkedInHref = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
  const xHref = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied to clipboard.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — copy the URL from your address bar.");
    }
  };

  return (
    <div className="relative bg-[#eef0e9] min-h-screen">
      <div className="grain" />
      <motion.div style={{ scaleX }} className="fixed top-0 left-0 right-0 h-[2px] bg-[#b5423a] origin-left z-[60]" />

      <article className="relative z-10 max-w-3xl mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-24">
        <button
          onClick={() => navigate("/", { state: { scrollTo: "writing" } })}
          data-testid="article-back"
          className="inline-flex items-center gap-2 font-mono-plex text-[12px] uppercase tracking-[0.14em] text-[#48606f] hover:text-[#16324f] mb-12"
        >
          <ArrowLeft size={14} /> Selected writing
        </button>

        <div className="flex items-center gap-4 flex-wrap">
          <span className="font-mono-plex text-[11px] uppercase tracking-[0.16em] text-[#b5423a]">{article.tag}</span>
          <span className="w-1 h-1 rounded-full bg-[#b5423a]" />
          <span className="font-mono-plex text-[12px] text-[#2e7d6b]">{article.meta}</span>
          <span className="w-1 h-1 rounded-full bg-[#b5423a]" />
          <span className="font-mono-plex text-[12px] text-[#48606f]">{article.date} · {article.readTime}</span>
        </div>

        <h1 className="font-serif-display font-light text-[#16324f] text-[clamp(2.4rem,6vw,4rem)] leading-[1.02] tracking-[-0.02em] mt-6">
          {article.title}
        </h1>
        <p className="mt-7 text-xl md:text-2xl font-serif-display font-light text-[#48606f] leading-snug">
          {article.excerpt}
        </p>

        {/* Share row */}
        <div className="flex items-center gap-3 mt-8 flex-wrap" data-testid="article-share">
          <span className="font-mono-plex text-[11px] uppercase tracking-[0.16em] text-[#48606f] mr-1">Share</span>
          <a
            data-testid="article-share-linkedin"
            href={linkedInHref}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Share on LinkedIn"
            className="w-9 h-9 rounded-full bg-[#0A66C2] text-white flex items-center justify-center hover:opacity-85 transition-opacity duration-300"
          >
            <FaLinkedinIn size={15} />
          </a>
          <a
            data-testid="article-share-x"
            href={xHref}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Share on X"
            className="w-9 h-9 rounded-full bg-black text-white flex items-center justify-center hover:opacity-85 transition-opacity duration-300"
          >
            <FaXTwitter size={15} />
          </a>
          <button
            data-testid="article-share-copy"
            onClick={copyLink}
            aria-label="Copy link"
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-[#c2c6bd] text-[#48606f] font-mono-plex text-[11px] uppercase tracking-[0.12em] hover:border-[#16324f] hover:text-[#16324f] transition-colors duration-300"
          >
            {copied ? <Check size={14} className="text-[#2e7d6b]" /> : <Link2 size={14} />}
            {copied ? "Copied" : "Copy link"}
          </button>
        </div>

        <div className="w-full h-px bg-[#d2d5ce] my-12" />

        <div className="space-y-6">
          {article.body.map((b) =>
            b.type === "h" ? (
              <h2 key={b.text} className="font-serif-display text-2xl md:text-3xl text-[#16324f] leading-tight pt-4">
                {b.text}
              </h2>
            ) : (
              <p key={b.text} className="text-[16.5px] md:text-[17px] text-[#3a4d5c] leading-[1.75]">
                {b.text}
              </p>
            )
          )}
        </div>

        {/* Shareable preview card */}
        <div className="mt-16" data-testid="article-preview-card">
          <span className="eyebrow">Share this piece</span>
          <div className="mt-4 rounded-2xl overflow-hidden border border-[#d2d5ce] bg-white">
            <div className="bg-[#16324f] px-7 py-6">
              <span className="font-mono-plex text-[11px] uppercase tracking-[0.18em] text-[#79d6bd]">
                {article.tag} · {article.readTime}
              </span>
              <h3 className="font-serif-display text-2xl md:text-3xl text-[#f5f6f2] leading-tight mt-2">
                {article.title}
              </h3>
            </div>
            <div className="px-7 py-6">
              <p className="text-[15px] text-[#3a4d5c] leading-relaxed line-clamp-3">{article.excerpt}</p>
              <div className="flex items-center justify-between gap-4 mt-5 flex-wrap">
                <div className="flex items-center gap-3">
                  <img src={PROFILE.logo} alt="MaCaja" className="h-10 w-auto object-contain" />
                  <div>
                    <p className="font-mono-plex text-[12px] text-[#16324f]">{PROFILE.name}</p>
                    <p className="font-mono-plex text-[11px] text-[#8b96a1]">{PROFILE.role}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2.5">
                  <a
                    href={linkedInHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Share on LinkedIn"
                    className="w-9 h-9 rounded-full bg-[#0A66C2] text-white flex items-center justify-center hover:opacity-85 transition-opacity duration-300"
                  >
                    <FaLinkedinIn size={15} />
                  </a>
                  <a
                    href={xHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Share on X"
                    className="w-9 h-9 rounded-full bg-black text-white flex items-center justify-center hover:opacity-85 transition-opacity duration-300"
                  >
                    <FaXTwitter size={15} />
                  </a>
                  <button
                    onClick={copyLink}
                    aria-label="Copy link"
                    className="w-9 h-9 rounded-full border border-[#c2c6bd] text-[#48606f] flex items-center justify-center hover:border-[#16324f] hover:text-[#16324f] transition-colors duration-300"
                  >
                    {copied ? <Check size={15} className="text-[#2e7d6b]" /> : <Link2 size={15} />}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 p-8 bg-[#f5f6f2] border border-[#d2d5ce]">
          <span className="eyebrow">Get in touch</span>
          <p className="mt-4 font-serif-display text-2xl text-[#16324f] leading-snug">
            Interested in this work? Catherine is open to public health & data roles worldwide.
          </p>
          <a
            href={`mailto:${PROFILE.email}`}
            className="group inline-flex items-center gap-2 mt-6 bg-[#16324f] text-[#f5f6f2] font-mono-plex text-[12.5px] uppercase tracking-[0.12em] px-6 py-3.5 rounded-full hover:bg-[#b5423a] transition-colors duration-400"
          >
            Email Catherine
            <ArrowUpRight size={15} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
          </a>
        </div>

        <Link to={`/writing/${next.slug}`} className="group block mt-14 pt-8 border-t border-[#d2d5ce]">
          <span className="font-mono-plex text-[11px] uppercase tracking-[0.16em] text-[#48606f]">Next</span>
          <div className="flex items-center justify-between gap-4 mt-2">
            <h3 className="font-serif-display text-2xl md:text-3xl text-[#16324f] group-hover:translate-x-1 transition-transform duration-400">
              {next.title}
            </h3>
            <ArrowUpRight size={24} className="text-[#b5423a] shrink-0" />
          </div>
        </Link>
      </article>
      <BackToTop />
    </div>
  );
}
